import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { isDesktop } from '../../app/desktop';
import { Wordmark } from '../../components/ui/Wordmark';
import { brand } from '../../config/brand';
import { AuthStage } from './AuthStage';

/**
 * The frame every auth screen shares: a crowd on one side, the form on the
 * other.
 *
 * ## Why it stopped being a card in the middle of nothing
 *
 * Because this is the one screen where somebody who has never used the site
 * decides whether it looks like anything. A centred card on a flat background
 * is what a form looks like when nobody has decided what the product is - and
 * this product has an answer, which is the people in the room. `AuthStage` puts
 * them there.
 *
 * ## The form is not inside the picture
 *
 * Two panes, not an overlay. Text over artwork is a contrast problem that has
 * to be re-solved every time the artwork changes, and this artwork changes
 * whenever somebody adds a hairstyle. A hard edge between them means the form
 * is on the page's own surface, at the page's own contrast, for ever.
 *
 * ## And on a phone there is only the form
 *
 * The stage is `hidden lg:block` rather than stacked above the form. Stacked,
 * it would push the email field below the fold on the one screen where the
 * fastest possible path to a filled-in form is the entire job. A phone gets the
 * lockup, the title and the fields, which is what it came for.
 *
 * ## Two ways out, on purpose
 *
 * These are the only screens with no header and no dock - see `BARE` in
 * `AppShell`, where the argument is that a header offering Discover is an
 * invitation to abandon the form. That argument holds and it had a hole in it:
 * somebody who lands here from a link had no way back to the site at all. So
 * the lockup is a link home, which is where anybody who has used a website
 * before will reach first, and there is a plain one at the bottom for anybody
 * who does not think to try.
 *
 * Both point at `/`, which is correct in either state: a stranger gets the
 * landing page and somebody already signed in - which `verify-email` and
 * `reset-password` both allow - gets their dashboard.
 *
 * ## Except in the desktop shell, where `/` is this screen
 *
 * `HomePage` sends a signed-out desktop reader here, because somebody who
 * installed an application does not need it explained. Which turns both of
 * these into a loop: the only two ways out of the sign-in lead to the sign-in.
 *
 * So on desktop they go to Discover instead. Not a detail - listening without
 * an account is the promise this platform makes on its own landing page, and a
 * desktop app that is stricter than the website would quietly stop making it
 * for the people most likely to have just found us.
 *
 * ## The lockup is drawn, not loaded
 *
 * It was an `<img>` of `mark.svg`, which bakes its colours in - including the
 * `#151530` tile behind the bars, a near-black that sat as a dark square on the
 * light theme while every other instance of the mark in the app followed the
 * theme. `Wordmark` draws the same shape from tokens. It also spells the name,
 * which the mark alone did not, and these are the pages where a visitor is most
 * likely to be meeting it for the first time.
 */

/**
 * Where the two exits go.
 *
 * `/` everywhere except the desktop shell, where `/` is this page - see the
 * note above.
 */
const WAY_OUT = isDesktop ? '/discover' : '/';

/** What the crowd is captioned with, when a page does not say. */
const PITCH = {
  headline: 'Everybody here has a face.',
  pitch:
    'Yours is one of them the moment you join, built rather than uploaded, and it is how a room sees you when you turn up to listen.',
};

export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  stage = PITCH,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * What the picture beside the form says.
   *
   * Per page, because the pages are not doing the same job: signing in is a
   * return and creating an account is an invitation, and one line cannot be
   * both without being about neither.
   */
  stage?: { headline: string; pitch: string };
}) {
  return (
    /*
      `self-stretch` because the shell centres a bare page inside a flex `main`.
      Without it this fills its content height and the stage becomes a band
      across the middle of the window rather than the side of it.
    */
    <div className="grid w-full self-stretch lg:grid-cols-[minmax(0,1fr)_minmax(0,34rem)]">
      <AuthStage headline={stage.headline} pitch={stage.pitch} />

      <div className="flex items-center justify-center px-6 py-14">
        <div className="flex w-full max-w-sm flex-col gap-7">
          <div className="flex flex-col items-center gap-4 text-center lg:items-start lg:text-left">
            <Link
              to={WAY_OUT}
              data-auth-home
              aria-label={`${brand.name} home`}
              className="rounded-[var(--radius-chip)] transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent"
            >
              <Wordmark className="h-9 w-auto" />
            </Link>

            <div className="flex flex-col gap-1.5">
              <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
              {subtitle && <p className="text-sm leading-relaxed text-soft">{subtitle}</p>}
            </div>
          </div>

          {/*
            No card any more.

            A bordered box inside a column that is already its own surface is a
            frame around a frame - it was there to hold the form together
            against an empty page, and the empty page is gone.
          */}
          {children}

          {footer && <div className="text-sm text-soft lg:text-left">{footer}</div>}

          {/*
            Quiet, and last. It is the way out rather than the thing to do here,
            so it sits below the reason somebody came and is styled to be found
            when looked for rather than to compete with the form.
          */}
          <Link
            to={WAY_OUT}
            data-auth-out
            className="font-mono text-[11px] text-faint transition-colors hover:text-ink"
          >
            ← {isDesktop ? 'Browse without an account' : `Back to ${brand.wordmark}`}
          </Link>
        </div>
      </div>
    </div>
  );
}
